import React from 'react';
import { motion } from 'framer-motion';
import '../styles/swiss-theme.css';
import ConceptGraphics from '../components/graphics/ConceptGraphics';
import SwissIcons from '../components/icons/SwissIconSystem';

const ReportPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-sand-50">
      {/* Hero Visual */}
      <div className="bg-ink-900 py-8">
        <div className="container-grid">
          <div className="grid-12">
            <div className="col-span-12">
              <ConceptGraphics type="hero-visual" className="w-full h-48" />
            </div>
          </div>
        </div>
      </div>

      {/* Report Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="container-grid py-12 border-b-2 border-ink-900"
      >
        <div className="grid-12">
          <div className="col-span-12">
            <h1 className="text-4xl lg:text-5xl font-light tracking-tight text-ink-900 mb-4">
              CLEAR Validation Report
            </h1>
            <p className="text-lg text-ink-600 font-light mb-6">
              Executive Summary · Crisis Learning, Early-warning, Anticipation, and Response Platform
            </p>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-8 pt-6 border-t border-ink-200">
              <div>
                <span className="block text-xs font-mono uppercase tracking-wider text-ink-500 mb-1">Report Type</span>
                <span className="block text-sm text-ink-900">Executive Summary</span>
              </div>
              <div>
                <span className="block text-xs font-mono uppercase tracking-wider text-ink-500 mb-1">Version</span>
                <span className="block text-sm text-ink-900">1.0</span>
              </div>
              <div>
                <span className="block text-xs font-mono uppercase tracking-wider text-ink-500 mb-1">Date</span>
                <span className="block text-sm text-ink-900">October 2025</span>
              </div>
              <div>
                <span className="block text-xs font-mono uppercase tracking-wider text-ink-500 mb-1">Format</span>
                <span className="block text-sm text-ink-900">Concise Executive</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* 01: Executive Summary */}
      <section className="container-grid py-12">
        <div className="grid-12">
          <div className="col-span-12">
            <h2 className="text-2xl font-medium text-ink-900 mb-6 pb-3 border-b-2 border-ink-900">
              <span className="inline-block text-accent-500 mr-3">01</span>
              Executive Summary
            </h2>

            <div className="bg-sand-100 border-l-4 border-accent-500 p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 pt-1">
                  <SwissIcons.Warning size={24} className="text-accent-500" />
                </div>
                <p className="text-sm leading-relaxed text-ink-900">
                  <strong>The Humanitarian Technology Paradox:</strong> Effective data management and technology tools have become critical for humanitarian response. The ecosystem is rapidly maturing, with AI-driven innovation (<a href="https://www.datakind.org/" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline font-medium">DataKind</a>, <a href="https://www.un.org/techenvoy/global-digital-compact" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline font-medium">UNGP</a>, academic prototypes) complementing established platforms (<a href="https://data.humdata.org/" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline font-medium">HDX</a>, <a href="https://reliefweb.int/" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline font-medium">ReliefWeb</a>, <a href="https://www.iom.int/" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline font-medium">IOM</a> Response Plans, <a href="https://www.acaps.org/" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline font-medium">ACAPS</a>). Yet responders are drowning in data yet starved of actionable intelligence due to fragmentation, information gaps, weak interoperability, and compounding crises.
                </p>
              </div>
            </div>

            <p className="text-sm leading-relaxed text-ink-900 mb-6">
              The Norwegian Refugee Council (NRC) and its partners face a paradox when it comes to data and digital technologies. On one hand, humanitarian operations generate enormous volumes of information; on the other, critical data gaps and poorly‑tailored tools force staff to resort to manual tracking, limiting the potential of data‑driven decision‑making. At the same time, artificial intelligence (AI) and digital tools are proliferating. Without governance and shared standards, adoption is fragmented; staff "shadow use" AI tools without institutional support. This report synthesises recent findings to help understand the landscape of data gaps, tool adoption challenges, AI uptake, coordination barriers, community participation and standardisation initiatives, and proposes strategic recommendations for a collaborative, user‑centric innovation ecosystem.
            </p>

            <h3 className="text-lg font-medium text-ink-900 mb-4 mt-8">Critical Market Gaps</h3>

            <ul className="space-y-3 mb-8">
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>Intelligence-to-Action Disconnect:</strong> Early warning systems predict crises but don't trigger operational responses—averaging 14-21 days before action
              </li>
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>System Fragmentation Crisis:</strong> Responders navigate 15-30 separate platforms per response, re-entering data 10+ times
              </li>
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>Manual Process Burden:</strong> 40-60% of professional time consumed by data collection and reporting rather than direct response
              </li>
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>Technology Dependence Risk:</strong> Critical infrastructure increasingly relies on commercial providers creating vendor lock-in risks
              </li>
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>AI Governance Gap:</strong> Over 40% of employees use generative AI without institutional guidance
              </li>
            </ul>

            <h3 className="text-lg font-medium text-ink-900 mb-4">The CLEAR Proposition</h3>

            <p className="text-sm leading-relaxed text-ink-900 mb-4">
              Built on the principle that <strong>effective response requires the right information at the right time, not all information all the time</strong>, CLEAR addresses three fundamental problems:
            </p>

            <ul className="space-y-3 mb-6">
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>Information Overload:</strong> Field teams drowning in uncoordinated data sources without systematic prioritization
              </li>
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>Coordination Fragmentation:</strong> Agencies duplicating efforts with redundant assessments despite coordination meetings
              </li>
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>Context Adaptation Delays:</strong> Generic tools requiring extensive customization for each crisis context
              </li>
            </ul>

            <p className="text-sm leading-relaxed text-ink-900">
              As one of only two platforms attempting end-to-end integration across the humanitarian value chain, CLEAR represents a strategic inflection point—transforming isolated excellence into coordinated impact through intelligent system design.
            </p>
          </div>
        </div>
      </section>

      {/* 02: Landscape of Existing Solutions */}
      <section className="container-grid py-12 bg-white">
        <div className="grid-12">
          <div className="col-span-12">
            <h2 className="text-2xl font-medium text-ink-900 mb-6 pb-3 border-b-2 border-ink-900">
              <span className="inline-block text-accent-500 mr-3">02</span>
              Landscape of Existing Solutions
            </h2>

            <p className="text-sm leading-relaxed text-ink-900 mb-6">
              The humanitarian technology ecosystem comprises 50+ specialized platforms serving distinct functions across the crisis response lifecycle. This analysis examines existing solutions through seven sequential phases that mirror the humanitarian value chain: <strong>Crisis Risk Awareness</strong>, <strong>Preparedness</strong>, <strong>Early Warning</strong>, <strong>Situation Analysis</strong>, <strong>Response Planning</strong>, <strong>Targeting</strong>, and <strong>Response Monitoring</strong>. While individual platforms demonstrate excellence within their domains, this systematic review reveals a critical pattern: isolated capability without integration infrastructure.
            </p>

            {/* Visual representation of platform ecosystem */}
            <div className="my-8">
              <ConceptGraphics type="data-mesh" className="w-full h-64" />
              <p className="text-xs text-center text-ink-500 mt-2 font-mono">
                Current Humanitarian Technology Ecosystem: Fragmented Excellence
              </p>
            </div>

            {/* 1. Crisis Risk Awareness */}
            <h3 className="text-lg font-medium text-ink-900 mb-4 mt-8">1. Crisis Risk Awareness</h3>
            <p className="text-sm leading-relaxed text-ink-900 mb-4">
              Forward-looking platforms that assess vulnerability and predict future crisis scenarios before they occur. These tools analyze long-term trends, structural vulnerabilities, and seasonal patterns to answer "What should we expect?"
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.Global size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://drmkc.jrc.ec.europa.eu/inform-index" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        INFORM Risk Index
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> 191 countries with multi-dimensional risk scoring</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Standardized hazard-vulnerability-capacity framework, open data access</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Annual updates only, macro-level focus not operational scale</p>
              </div>

              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.TrendAnalysis size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://fews.net/" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        FEWS NET (Famine Early Warning)
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> 40+ years of food security data covering 36 countries</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Long-term trend analysis, expert-driven seasonal forecasting</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Monthly updates too slow for rapid-onset crises</p>
              </div>
            </div>

            {/* 2. Preparedness */}
            <h3 className="text-lg font-medium text-ink-900 mb-4 mt-8">2. Preparedness</h3>
            <p className="text-sm leading-relaxed text-ink-900 mb-4">
              Strategic planning platforms that answer "How should we prepare?" by supporting contingency planning, scenario modeling, and anticipatory action frameworks.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.Message size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://www.who.int/teams/risk-communication" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        RCCE (Risk Communication & Community Engagement)
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> Community engagement frameworks for public health and disaster preparedness</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Two-way communication strategies, behavioral insights</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Framework-level guidance without operational platform integration</p>
              </div>

              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.Analytics size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://www.510.global/" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        Impact-Based Forecasting (IBF/510)
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> Anticipatory action triggers based on forecast-based financing protocols</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Automated trigger protocols linking forecasts to pre-approved funding</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Limited to specific hazard types with reliable forecasting</p>
              </div>
            </div>

            {/* 3. Early Warning */}
            <h3 className="text-lg font-medium text-ink-900 mb-4 mt-8">3. Early Warning</h3>
            <p className="text-sm leading-relaxed text-ink-900 mb-4">
              Real-time detection systems monitoring for immediate crisis threats using sensors, satellite imagery, and AI-powered signal processing.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.Emergency size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://www.gdacs.org/" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        GDACS (Global Disaster Alert)
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> Real-time disaster detection within minutes, global multi-hazard monitoring</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Automated alerting from satellite imagery and seismic sensors</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Alert-only system requiring manual interpretation</p>
              </div>

              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.Search size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://www.google.org/crisisresponse/" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        Google Crisis Response
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> Flood Hub, wildfire tracking, earthquake alerts with global predictive modeling</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Advanced predictive analytics, public notifications</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Hazard-specific tools not integrated with humanitarian coordination</p>
              </div>

              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.Protection size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://halasystems.com/" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        Hala Systems
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> Real-time conflict early warning system in Syria</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Minutes-advance warning of airstrikes, proven life-saving impact</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Conflict-specific, geographic limitation to active war zones</p>
              </div>

              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.Critical size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://www.dataminr.com/" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        Dataminr First Alert
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> Real-time event detection from 500,000+ public data sources globally</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Breaking event detection minutes before news media</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Commercial platform, alert-only without humanitarian workflow integration</p>
              </div>
            </div>

            {/* 4. Situation Analysis */}
            <h3 className="text-lg font-medium text-ink-900 mb-4 mt-8">4. Situation Analysis</h3>
            <p className="text-sm leading-relaxed text-ink-900 mb-4">
              Information aggregation and synthesis platforms that answer "What's the impact?" by consolidating diverse data sources into comprehensive situational understanding.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.Database size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://data.humdata.org/" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        HDX (Humanitarian Data Exchange)
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> 19,000+ datasets from 300+ organizations, 1.5M+ users</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Open API with standardized formats, machine-readable data repository</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Passive repository with no operational integration or real-time coordination</p>
              </div>

              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.Document size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://reliefweb.int/" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        ReliefWeb
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> 17M+ users, daily updates from 4,000+ sources</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Information curation and dissemination, humanitarian news hub</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Content portal only, no coordination tools or operational workflow integration</p>
              </div>

              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.Alert size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://acleddata.com/" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        ACLED (Armed Conflict Location & Event Data)
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> 1.5M+ conflict events mapped, daily geospatial updates</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Real-time conflict tracking, predictive modeling with CAST system</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Conflict-specific focus, no multi-hazard integration</p>
              </div>

              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.Research size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://www.acaps.org/" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        ACAPS (Analysis Capacity Project)
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> 150+ countries monitored with standardized needs assessment methodology</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Systematic crisis analysis, publicly accessible reports</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Manual process with periodic updates (not real-time)</p>
              </div>
            </div>

            {/* 5. Response Planning */}
            <h3 className="text-lg font-medium text-ink-900 mb-4 mt-8">5. Response Planning</h3>
            <p className="text-sm leading-relaxed text-ink-900 mb-4">
              Operational coordination platforms that answer "What should we do about it?" by facilitating inter-agency planning, resource allocation, and tactical decision-making.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.Coordination size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://www.disastercoordinationhub.org/" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        DCH (Disaster Coordination Hub)
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> Community leaders, nonprofits, government coordination for disaster response</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Real-time crisis coordination platform, resource deployment tools</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Focus on disaster-specific coordination, requires partner enrollment</p>
              </div>

              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.Form size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://www.kobotoolbox.org/" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        KoboToolbox
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> 400M+ data submissions (2024), 500K+ users globally</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Mobile data collection, offline-capable with synchronization</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Data collection only, no analysis engine or coordination integration</p>
              </div>
            </div>

            {/* 6. Targeting */}
            <h3 className="text-lg font-medium text-ink-900 mb-4 mt-8">6. Targeting</h3>
            <p className="text-sm leading-relaxed text-ink-900 mb-4">
              Beneficiary identification and registration platforms that answer "Who needs what?" by managing individual-level data for assistance delivery.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.Users size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://www.wfp.org/scope" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        WFP SCOPE
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> 50M+ beneficiaries served globally, largest humanitarian delivery platform</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Cash transfer and food assistance, biometric registration</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Manual triggering required, weeks between crisis detection and payment activation</p>
              </div>

              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.Displacement size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://www.unhcr.org/primes" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        UNHCR PRIMES
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> 28M+ registered individuals (2023), multi-country identity management</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Large-scale identity management, refugee registration</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Siloed within UNHCR, limited cross-agency access due to privacy concerns</p>
              </div>
            </div>

            {/* 7. Response Monitoring */}
            <h3 className="text-lg font-medium text-ink-900 mb-4 mt-8">7. Response Monitoring</h3>
            <p className="text-sm leading-relaxed text-ink-900 mb-4">
              Impact tracking and accountability platforms that answer "What is our impact?" by monitoring program delivery, measuring outcomes, and tracking resource utilization.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.Analytics size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://fts.unocha.org/" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        FTS (Financial Tracking Service)
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> $30B+ humanitarian funding tracked annually (2024)</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Financial transparency, resource allocation tracking</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Financial data only, no linkage to operational needs or programmatic outcomes</p>
              </div>

              <div className="border border-ink-200 p-4 hover:border-accent-500 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SwissIcons.Checklist size={16} className="text-accent-500" />
                    <h4 className="text-sm font-medium text-ink-900">
                      <a href="https://www.activityinfo.org/" target="_blank" rel="noopener noreferrer" className="hover:text-accent-500">
                        ActivityInfo
                      </a>
                    </h4>
                  </div>
                  <SwissIcons.ArrowRight size={14} className="text-ink-400" />
                </div>
                <p className="text-xs text-ink-600 mb-2"><strong>Coverage:</strong> Project monitoring and reporting for humanitarian programs</p>
                <p className="text-xs text-ink-600 mb-2"><strong>Capability:</strong> Customizable indicators, offline data collection</p>
                <p className="text-xs text-ink-900"><strong>Limitation:</strong> Organization-level reporting without cross-agency aggregation</p>
              </div>
            </div>

            {/* Key Findings */}
            <div className="bg-sand-100 border-l-4 border-accent-500 p-6 mt-8">
              <p className="text-sm leading-relaxed text-ink-900 mb-3">
                <strong>The Fragmentation Pattern:</strong> While individual platforms demonstrate excellence within their domains—HDX with 19,000+ datasets, SCOPE serving 50M+ beneficiaries, ACLED with 1.5M+ conflict events mapped—only <strong>11% provide API integration</strong>, and <strong>none offer end-to-end workflows</strong> from detection through delivery.
              </p>
              <p className="text-sm leading-relaxed text-ink-900">
                <strong>CLEAR's Strategic Position:</strong> As one of only two platforms attempting full integration, CLEAR complements rather than competes with existing solutions by providing the connective infrastructure these specialized platforms lack.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Divider */}
      <div className="bg-ink-900 py-1"></div>

      {/* 03: Critical Gaps Analysis */}
      <section className="container-grid py-12">
        <div className="grid-12">
          <div className="col-span-12">
            <h2 className="text-2xl font-medium text-ink-900 mb-6 pb-3 border-b-2 border-ink-900">
              <span className="inline-block text-accent-500 mr-3">03</span>
              Critical Gaps Analysis
            </h2>

            {/* Visual: Hours Matter */}
            <div className="mb-8">
              <ConceptGraphics type="rapid-deployment" className="w-full h-56" />
              <p className="text-xs text-center text-ink-500 mt-2 font-mono">
                Time is Critical: From Detection to Assistance Delivery
              </p>
            </div>

            <h3 className="text-lg font-medium text-ink-900 mb-4">System Fragmentation</h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <div className="border border-ink-200 p-4 bg-white hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3 mb-2">
                  <SwissIcons.Database size={18} className="text-accent-500 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-ink-900 mb-2">
                      API & Interoperability <span className="ml-2 px-2 py-1 bg-accent-500 text-white text-xs">High</span>
                    </h4>
                    <p className="text-xs text-ink-600 mb-2">Only 11% of platforms provide standardized APIs. Different data formats, authentication mechanisms, and update protocols.</p>
                    <p className="text-xs text-ink-900">Manual data reconciliation required, delays in coordination, increased error rates</p>
                  </div>
                </div>
              </div>

              <div className="border border-ink-200 p-4 bg-white hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3 mb-2">
                  <SwissIcons.Clock size={18} className="text-accent-500 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-ink-900 mb-2">
                      Real-Time Capability <span className="ml-2 px-2 py-1 bg-accent-500 text-white text-xs">High</span>
                    </h4>
                    <p className="text-xs text-ink-600 mb-2">Most platforms update monthly or quarterly. Average time from alert to response: 14-21 days. 23 manual touchpoints required.</p>
                    <p className="text-xs text-ink-900">Decisions sometimes based on outdated information</p>
                  </div>
                </div>
              </div>

              <div className="border border-ink-200 p-4 bg-white hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3 mb-2">
                  <SwissIcons.Coordination size={18} className="text-accent-500 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-ink-900 mb-2">
                      End-to-End Integration <span className="ml-2 px-2 py-1 bg-accent-500 text-white text-xs">High</span>
                    </h4>
                    <p className="text-xs text-ink-600 mb-2">Detection, analysis, coordination, and delivery systems operate independently with manual handoffs.</p>
                    <p className="text-xs text-ink-900">72+ hour response time, information loss at handoffs, inability to implement anticipatory action</p>
                  </div>
                </div>
              </div>

              <div className="border border-ink-200 p-4 bg-white hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3 mb-2">
                  <SwissIcons.Warning size={18} className="text-accent-500 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-ink-900 mb-2">
                      Data Quality & Bias <span className="ml-2 px-2 py-1 bg-accent-500 text-white text-xs">High</span>
                    </h4>
                    <p className="text-xs text-ink-600 mb-2">Data sources skewed toward accessible populations. Vulnerable communities underrepresented. AI models trained on biased data.</p>
                    <p className="text-xs text-ink-900">Skewed outputs reinforcing inequities. Field offices lack capacity to interact with sophisticated systems</p>
                  </div>
                </div>
              </div>
            </div>

            <h3 className="text-lg font-medium text-ink-900 mb-4 mt-8">Operational Challenges</h3>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="border border-ink-200 p-6 text-center">
                <div className="text-4xl font-light text-accent-500 mb-2">15-30</div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Systems Per Response</div>
              </div>
              <div className="border border-ink-200 p-6 text-center">
                <div className="text-4xl font-light text-accent-500 mb-2">10+</div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Data Re-entries</div>
              </div>
              <div className="border border-ink-200 p-6 text-center">
                <div className="text-4xl font-light text-accent-500 mb-2">23+</div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Manual Touchpoints</div>
              </div>
              <div className="border border-ink-200 p-6 text-center">
                <div className="text-4xl font-light text-accent-500 mb-2">11%</div>
                <div className="text-xs uppercase tracking-wider text-ink-500">API Integration</div>
              </div>
            </div>

            <div className="bg-sand-100 border-l-4 border-accent-500 p-6">
              <p className="text-sm leading-relaxed text-ink-900 mb-3">
                <strong>The Integration Imperative:</strong> These gaps cannot be addressed in isolation. Improving detection alone doesn't help if data infrastructure can't support real-time streaming. Better data infrastructure means nothing if situation analysis tools remain fragmented. Excellent situation analysis is useless if cash distribution requires manual triggering weeks later.
              </p>
              <p className="text-sm leading-relaxed text-ink-900">
                <strong>Integration is not optional—it is the solution.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Divider */}
      <div className="bg-ink-900 py-1"></div>

      {/* 04: CLEAR Solution Validation */}
      <section className="container-grid py-12 bg-white">
        <div className="grid-12">
          <div className="col-span-12">
            <h2 className="text-2xl font-medium text-ink-900 mb-6 pb-3 border-b-2 border-ink-900">
              <span className="inline-block text-accent-500 mr-3">04</span>
              CLEAR Solution Validation
            </h2>

            {/* CLEAR System Visual */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <div>
                <ConceptGraphics type="crisis-detection" className="w-full h-48" />
                <p className="text-xs text-center text-ink-500 mt-2 font-mono">
                  AI-Powered Crisis Detection Engine
                </p>
              </div>
              <div>
                <ConceptGraphics type="situation-analysis" className="w-full h-48" />
                <p className="text-xs text-center text-ink-500 mt-2 font-mono">
                  Real-Time Situation Analysis Dashboard
                </p>
              </div>
            </div>

            <h3 className="text-lg font-medium text-ink-900 mb-4">Strategic Positioning</h3>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <div className="border border-ink-200 p-6 text-center">
                <div className="text-4xl font-light text-accent-500 mb-2">1 of 2</div>
                <div className="text-xs uppercase tracking-wider text-ink-500">End-to-End Platforms</div>
              </div>
              <div className="border border-ink-200 p-6 text-center">
                <div className="text-4xl font-light text-accent-500 mb-2">&lt;24h</div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Target Response Time</div>
              </div>
              <div className="border border-ink-200 p-6 text-center">
                <div className="text-4xl font-light text-accent-500 mb-2">100%</div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Open Architecture</div>
              </div>
              <div className="border border-ink-200 p-6 text-center">
                <div className="text-4xl font-light text-accent-500 mb-2">4</div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Integrated Components</div>
              </div>
            </div>

            <h3 className="text-lg font-medium text-ink-900 mb-6">CLEAR's Four Integrated Components</h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
              <div className="border border-ink-200 p-6">
                <h4 className="text-sm font-medium uppercase tracking-wider text-ink-900 mb-3 pb-2 border-b border-ink-200">
                  1. Crisis Detection Engine
                </h4>
                <p className="text-xs text-ink-600 mb-3">
                  Transforms early warning from passive alerts into actionable intelligence by aggregating multi-source crisis signals and providing probability-based scenario modeling. Integrates satellite imagery, social media, field reports, and sensor data to detect emerging threats minutes to hours before traditional systems.
                </p>
                <ul className="space-y-1 text-xs text-ink-900">
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Real-time multi-hazard monitoring (conflict, disaster, disease, food insecurity)
                  </li>
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    AI-powered pattern recognition across 10,000+ signals per second
                  </li>
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Community-based early warning integration with ERRs and local responders
                  </li>
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Confidence scoring with quantified likelihood assessments
                  </li>
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Automated alert triggering for high-confidence scenarios
                  </li>
                </ul>
              </div>

              <div className="border border-ink-200 p-6">
                <h4 className="text-sm font-medium uppercase tracking-wider text-ink-900 mb-3 pb-2 border-b border-ink-200">
                  2. Situation Analysis
                </h4>
                <p className="text-xs text-ink-600 mb-3">
                  Converts raw crisis data into operational intelligence through AI-powered needs assessment and vulnerability analysis. Eliminates 10+ data re-entries by serving as the unified analytical layer connecting detection systems to response planning, updating continuously rather than monthly.
                </p>
                <ul className="space-y-1 text-xs text-ink-900">
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Automated needs assessment with AI-driven vulnerability scoring
                  </li>
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Real-time affected population estimation with confidence intervals
                  </li>
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Multi-sectoral impact analysis (health, shelter, protection, livelihoods)
                  </li>
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Geographic targeting with accessibility and security overlays
                  </li>
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Duplicate prevention through cross-organization data sharing
                  </li>
                </ul>
              </div>

              <div className="border border-ink-200 p-6">
                <h4 className="text-sm font-medium uppercase tracking-wider text-ink-900 mb-3 pb-2 border-b border-ink-200">
                  3. Response Console
                </h4>
                <p className="text-xs text-ink-600 mb-3">
                  Enables inter-agency coordination through secure information sharing and unified operational planning. Replaces email chains and coordination meetings with real-time collaborative platform, allowing organizations to identify gaps, prevent duplication, and coordinate multi-sectoral response without compromising data sovereignty.
                </p>
                <ul className="space-y-1 text-xs text-ink-900">
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Unified operational dashboard replacing 15-30 disconnected systems
                  </li>
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Secure cross-agency referral system with trust-based networks
                  </li>
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Automated 3W (Who, What, Where) coordination preventing duplication
                  </li>
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Resource matching across consortium partner warehouses and stockpiles
                  </li>
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Offline-capable mobile interface for field coordination
                  </li>
                </ul>
              </div>

              <div className="border border-ink-200 p-6">
                <h4 className="text-sm font-medium uppercase tracking-wider text-ink-900 mb-3 pb-2 border-b border-ink-200">
                  4. Rapid Response
                </h4>
                <p className="text-xs text-ink-600 mb-3">
                  Automates the crisis-to-cash pipeline by integrating directly with delivery platforms like WFP SCOPE and mobile money systems. Compresses the 14-21 day delay between crisis detection and assistance activation to under 24 hours through automated eligibility determination, digital registration, and instant payment triggering.
                </p>
                <ul className="space-y-1 text-xs text-ink-900">
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Automated beneficiary eligibility determination with vulnerability scoring
                  </li>
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Direct API integration with SCOPE, mobile money, and banking systems
                  </li>
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Digital registration via QR codes, biometrics, or existing ID systems
                  </li>
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Instant payment triggering for pre-approved crisis scenarios
                  </li>
                  <li className="pl-3 relative before:content-['▸'] before:absolute before:left-0 before:text-accent-500">
                    Real-time distribution monitoring with SMS-based post-delivery feedback
                  </li>
                </ul>
              </div>
            </div>

            <h3 className="text-lg font-medium text-ink-900 mb-4">Expected Impact</h3>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="border border-ink-200 p-6 text-center">
                <div className="text-4xl font-light text-accent-500 mb-2">95%</div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Time Reduction</div>
              </div>
              <div className="border border-ink-200 p-6 text-center">
                <div className="text-4xl font-light text-accent-500 mb-2">23→0</div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Manual Handoffs Eliminated</div>
              </div>
              <div className="border border-ink-200 p-6 text-center">
                <div className="text-4xl font-light text-accent-500 mb-2">80%</div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Duplicate Reduction</div>
              </div>
              <div className="border border-ink-200 p-6 text-center">
                <div className="text-4xl font-light text-accent-500 mb-2">15-30→1</div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Systems Unified</div>
              </div>
            </div>

            <h3 className="text-lg font-medium text-ink-900 mb-4">Unique Value Propositions</h3>

            <ul className="space-y-3 mb-8">
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>End-to-End Integration:</strong> Unified workflow from crisis detection through resource delivery, eliminating manual handoffs
              </li>
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>Real-Time Operations:</strong> Sub-minute crisis detection and alerting, enabling anticipatory action triggers
              </li>
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>AI-Powered Automation:</strong> Automated needs assessment, predictive analytics, intelligent resource allocation
              </li>
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>Cross-Hazard Monitoring:</strong> Unified monitoring across natural disasters, conflict, disease, economic shocks
              </li>
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>Learning System:</strong> Systematic capture of response outcomes, continuous improvement of predictive models
              </li>
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>Neutral Governance:</strong> Multi-stakeholder governance avoiding agency territoriality, open architecture
              </li>
            </ul>

            <div className="bg-sand-100 border-l-4 border-accent-500 p-6">
              <p className="text-sm leading-relaxed text-ink-900">
                <strong>Validation Summary:</strong> CLEAR addresses critical, validated gaps in the humanitarian technology ecosystem. Its end-to-end integration approach fills a market void that existing platforms do not address. The technical architecture is sound, the strategic positioning is differentiated, and the value proposition is clear.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Divider */}
      <div className="bg-ink-900 py-1"></div>

      {/* 05: Key Recommendations */}
      <section className="container-grid py-12">
        <div className="grid-12">
          <div className="col-span-12">
            <h2 className="text-2xl font-medium text-ink-900 mb-6 pb-3 border-b-2 border-ink-900">
              <span className="inline-block text-accent-500 mr-3">05</span>
              Key Recommendations
            </h2>

            <div className="space-y-6">
              <div className="grid grid-cols-12 gap-6 border border-ink-200 p-6 bg-white hover:shadow-md transition-shadow">
                <div className="col-span-2 text-center">
                  <SwissIcons.Users size={32} className="text-accent-500 mx-auto mb-2" />
                  <div className="text-4xl font-light text-accent-500">01</div>
                </div>
                <div className="col-span-10">
                  <h4 className="text-sm font-medium uppercase tracking-wider text-ink-900 mb-2">Establish Multi-Stakeholder Governance</h4>
                  <p className="text-xs text-accent-500 mb-2">Priority: Critical | Timeline: Immediate</p>
                  <p className="text-sm text-ink-900">
                    Create neutral governance structure with representation from UN agencies, NGOs, affected communities, technical partners, and donors. Form technical advisory board, establish clear decision-making processes, define principles for data governance and ethical AI use.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-6 border border-ink-200 p-6 bg-white hover:shadow-md transition-shadow">
                <div className="col-span-2 text-center">
                  <SwissIcons.Globe size={32} className="text-accent-500 mx-auto mb-2" />
                  <div className="text-4xl font-light text-accent-500">02</div>
                </div>
                <div className="col-span-10">
                  <h4 className="text-sm font-medium uppercase tracking-wider text-ink-900 mb-2">Prioritize Open Architecture & Interoperability</h4>
                  <p className="text-xs text-accent-500 mb-2">Priority: Critical | Timeline: Immediate</p>
                  <p className="text-sm text-ink-900">
                    Design CLEAR as complementary infrastructure rather than competitive replacement. Publish comprehensive API documentation, support humanitarian data standards (HXL, IATI, OCHA), build adapter layers for <a href="https://data.humdata.org/" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline font-medium">HDX</a>, <a href="https://www.acaps.org/" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline font-medium">ACAPS</a>, <a href="https://www.wfp.org/scope" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline font-medium">SCOPE</a>, and other key platforms.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-6 border border-ink-200 p-6 bg-white hover:shadow-md transition-shadow">
                <div className="col-span-2 text-center">
                  <SwissIcons.Strategic size={32} className="text-accent-500 mx-auto mb-2" />
                  <div className="text-4xl font-light text-accent-500">03</div>
                </div>
                <div className="col-span-10">
                  <h4 className="text-sm font-medium uppercase tracking-wider text-ink-900 mb-2">Implement Field-First Phased Deployment</h4>
                  <p className="text-xs text-accent-500 mb-2">Priority: High | Timeline: 0-36 months</p>
                  <p className="text-sm text-ink-900">
                    <strong>Year 1:</strong> Deploy Crisis Detection Engine in Sudan, establish data infrastructure with 3-5 core organizations, launch Field Interface prototype. <strong>Year 2:</strong> Expand to 3 crisis types, implement full inter-agency referral system, achieve 80% reduction in duplicate assessments. <strong>Year 3:</strong> Full multi-organization deployment, sustainable funding model, 80% of maintenance capabilities within humanitarian organizations.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-6 border border-ink-200 p-6 bg-white hover:shadow-md transition-shadow">
                <div className="col-span-2 text-center">
                  <SwissIcons.FieldWorker size={32} className="text-accent-500 mx-auto mb-2" />
                  <div className="text-4xl font-light text-accent-500">04</div>
                </div>
                <div className="col-span-10">
                  <h4 className="text-sm font-medium uppercase tracking-wider text-ink-900 mb-2">Invest in User-Centered Design for Field Conditions</h4>
                  <p className="text-xs text-accent-500 mb-2">Priority: High | Timeline: Ongoing</p>
                  <p className="text-sm text-ink-900">
                    Ensure CLEAR works in real-world humanitarian contexts: offline-first architecture, mobile-responsive design, localization with major humanitarian languages, comprehensive training programs, progressive disclosure of complexity, regular usability testing with field teams.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-6 border border-ink-200 p-6 bg-white hover:shadow-md transition-shadow">
                <div className="col-span-2 text-center">
                  <SwissIcons.Analytics size={32} className="text-accent-500 mx-auto mb-2" />
                  <div className="text-4xl font-light text-accent-500">05</div>
                </div>
                <div className="col-span-10">
                  <h4 className="text-sm font-medium uppercase tracking-wider text-ink-900 mb-2">Develop Sustainable Funding Model</h4>
                  <p className="text-xs text-accent-500 mb-2">Priority: Critical | Timeline: 0-6 months</p>
                  <p className="text-sm text-ink-900">
                    Diversified approach: multi-donor pooled funding for core services, cost-recovery for premium features, long-term institutional commitments, demonstrated ROI through reduced response time and coordination costs, position as digital infrastructure worthy of sustained investment.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-6 border border-ink-200 p-6 bg-white hover:shadow-md transition-shadow">
                <div className="col-span-2 text-center">
                  <SwissIcons.Protection size={32} className="text-accent-500 mx-auto mb-2" />
                  <div className="text-4xl font-light text-accent-500">06</div>
                </div>
                <div className="col-span-10">
                  <h4 className="text-sm font-medium uppercase tracking-wider text-ink-900 mb-2">Implement Robust Data Protection & Ethical AI</h4>
                  <p className="text-xs text-accent-500 mb-2">Priority: Critical | Timeline: Immediate</p>
                  <p className="text-sm text-ink-900">
                    Privacy-by-design architecture, end-to-end encryption, explicit consent mechanisms, bias detection and correction in ML models, regular algorithmic audits, clear data retention policies, compliance with humanitarian data protection standards, "do no harm" framework for AI deployment.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-6 border border-ink-200 p-6 bg-white hover:shadow-md transition-shadow">
                <div className="col-span-2 text-center">
                  <SwissIcons.TrendAnalysis size={32} className="text-accent-500 mx-auto mb-2" />
                  <div className="text-4xl font-light text-accent-500">07</div>
                </div>
                <div className="col-span-10">
                  <h4 className="text-sm font-medium uppercase tracking-wider text-ink-900 mb-2">Generate and Publish Impact Evidence</h4>
                  <p className="text-xs text-accent-500 mb-2">Priority: High | Timeline: Ongoing from Phase 1</p>
                  <p className="text-sm text-ink-900">
                    Systematic measurement and public reporting: response time metrics, coordination efficiency, cost analysis, quality measures, user satisfaction, humanitarian outcomes. Evidence framework enables continuous improvement and demonstrates value to stakeholders.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-6 border border-ink-200 p-6 bg-white hover:shadow-md transition-shadow">
                <div className="col-span-2 text-center">
                  <SwissIcons.Coordination size={32} className="text-accent-500 mx-auto mb-2" />
                  <div className="text-4xl font-light text-accent-500">08</div>
                </div>
                <div className="col-span-10">
                  <h4 className="text-sm font-medium uppercase tracking-wider text-ink-900 mb-2">Build Strategic Partnerships with Existing Platforms</h4>
                  <p className="text-xs text-accent-500 mb-2">Priority: High | Timeline: 0-6 months</p>
                  <p className="text-sm text-ink-900">
                    Proactive collaboration with <a href="https://data.humdata.org/" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline font-medium">HDX</a>, <a href="https://www.acaps.org/" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline font-medium">ACAPS</a>, <a href="https://www.wfp.org/scope" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline font-medium">SCOPE</a> to demonstrate complementarity rather than competition. Technical integration agreements, joint pilots demonstrating integrated workflows, shared governance participation, co-development of interoperability standards.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Divider */}
      <div className="bg-ink-900 py-1"></div>

      {/* 06: Conclusion */}
      <section className="container-grid py-12 bg-white">
        <div className="grid-12">
          <div className="col-span-12">
            <h2 className="text-2xl font-medium text-ink-900 mb-6 pb-3 border-b-2 border-ink-900">
              <span className="inline-block text-accent-500 mr-3">06</span>
              Conclusion
            </h2>

            {/* Visual Impact Statement */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <div>
                <ConceptGraphics type="hours-matter" className="w-full h-48" />
                <p className="text-xs text-center text-ink-500 mt-2 font-mono">
                  Every Hour Counts in Crisis Response
                </p>
              </div>
              <div className="flex flex-col justify-center p-6 bg-sand-100">
                <div className="flex items-center gap-4 mb-4">
                  <SwissIcons.Urgent size={32} className="text-accent-500" />
                  <div>
                    <div className="text-3xl font-light text-accent-500">339M</div>
                    <div className="text-xs text-ink-600">People needing assistance (2023)</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <SwissIcons.Clock size={32} className="text-accent-500" />
                  <div>
                    <div className="text-3xl font-light text-accent-500">14-21</div>
                    <div className="text-xs text-ink-600">Days average response delay</div>
                  </div>
                </div>
              </div>
            </div>

            <h3 className="text-lg font-medium text-ink-900 mb-4">The Cost of Fragmentation</h3>

            <p className="text-sm leading-relaxed text-ink-900 mb-4">
              Humanitarian organizations face a critical paradox: more data and AI tools than ever before, yet critical gaps, fragmented adoption, and coordination barriers persist. Evidence conclusively validates the need for integration:
            </p>

            <ul className="space-y-3 mb-8">
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>The Problem is Real and Quantified:</strong> 339M people needed assistance in 2023 while responders navigate 15-30 systems. Field coordinators spend 60% of time finding and sharing information rather than delivering aid
              </li>
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>Current Solutions are Insufficient:</strong> Despite individual strengths—HDX's 19,000+ datasets, SCOPE's 50M+ beneficiaries, ACLED's 1.5M+ conflict events mapped—no solution provides end-to-end integration
              </li>
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>Speed Determines Survival:</strong> Average response time: 14-21 days. Yet 24-hour advance warning reduces disaster damage by 30%, and the first 72 hours after disaster onset are critical for saving lives
              </li>
              <li className="text-sm pl-4 border-l-2 border-accent-500">
                <strong>AI Adoption Outpaces Governance:</strong> Over 40% of employees use generative AI without institutional guidance
              </li>
            </ul>

            <h3 className="text-lg font-medium text-ink-900 mb-4">The Moral Imperative</h3>

            <p className="text-sm leading-relaxed text-ink-900 mb-6">
              As crises grow more complex and frequent, the cost of fragmentation becomes unconscionable. The humanitarian sector stands at a critical window: <strong>act now to build sector-owned AI infrastructure, or risk permanent dependency on commercial systems it cannot control or modify.</strong>
            </p>

            <div className="bg-sand-100 border-l-4 border-accent-500 p-6 mb-8">
              <p className="text-sm leading-relaxed text-ink-900">
                <strong>The Vision:</strong> A world where humanitarian response is as fast as the crises we face—where early warning triggers immediate action, where aid reaches those who need it most within hours not weeks, and where every dollar saved through efficiency means another life protected.
              </p>
            </div>

            <h3 className="text-lg font-medium text-ink-900 mb-4">The Transformation CLEAR Enables</h3>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="border border-ink-200 p-6 text-center">
                <div className="text-4xl font-light text-accent-500 mb-2">95%</div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Response Time Reduction</div>
              </div>
              <div className="border border-ink-200 p-6 text-center">
                <div className="text-4xl font-light text-accent-500 mb-2">75%</div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Processing Efficiency</div>
              </div>
              <div className="border border-ink-200 p-6 text-center">
                <div className="text-4xl font-light text-accent-500 mb-2">80%</div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Duplicate Elimination</div>
              </div>
              <div className="border border-ink-200 p-6 text-center">
                <div className="text-4xl font-light text-accent-500 mb-2">24h</div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Target Response Time</div>
              </div>
            </div>

            <p className="text-sm leading-relaxed text-ink-900 mb-4">
              <strong>From Weeks to Hours:</strong> Aid delivery compressed from 2-4 weeks to under 24 hours through automated workflows and integrated systems.
            </p>

            <p className="text-sm leading-relaxed text-ink-900 mb-4">
              <strong>From Fragmented to Unified:</strong> Single intelligent ecosystem replacing 15-30 disconnected systems that currently consume 60% of field coordinators' time.
            </p>

            <p className="text-sm leading-relaxed text-ink-900 mb-4">
              <strong>From Reactive to Anticipatory:</strong> Automated scenario-based alerts and action triggers enable response before crisis peaks, leveraging proven ROI where 24-hour advance warning reduces disaster damage by 30%.
            </p>

            <p className="text-sm leading-relaxed text-ink-900 mb-6">
              <strong>From Manual to Automated:</strong> Reducing operational overhead by 40-60%, allowing humanitarian resources to focus on direct assistance rather than information management.
            </p>

            <div className="bg-sand-100 border-l-4 border-accent-500 p-6">
              <p className="text-sm leading-relaxed text-ink-900 mb-3">
                <strong>The Path Forward:</strong> CLEAR provides a technically sound, strategically positioned, and operationally necessary solution to the integration crisis facing humanitarian response. Success requires implementation courage and ecosystem collaboration—but the alternative is acceptance of preventable delays, inefficiencies, and ultimately, preventable suffering.
              </p>
              <p className="text-sm leading-relaxed text-ink-900">
                <strong>The question before the humanitarian sector:</strong> Will institutional will match technological capability in service of humanity's most vulnerable? The humanitarian technology ecosystem has proven it can innovate. Now it must prove it can integrate.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Divider */}
      <div className="bg-ink-900 py-1"></div>

      {/* 07: References */}
      <section className="container-grid py-12">
        <div className="grid-12">
          <div className="col-span-12">
            <h2 className="text-2xl font-medium text-ink-900 mb-6 pb-3 border-b-2 border-ink-900">
              <span className="inline-block text-accent-500 mr-3">07</span>
              References & Data Sources
            </h2>

            <div className="flex items-center gap-3 mb-6 p-4 bg-sand-100">
              <SwissIcons.Document size={20} className="text-accent-500" />
              <p className="text-xs text-ink-700">
                All sources have been verified and linked where publicly accessible. Click any reference to access the original source.
              </p>
            </div>

            <h3 className="text-base font-medium text-ink-900 mb-3 flex items-center gap-2">
              <SwissIcons.Global size={16} className="text-accent-500" />
              Early Warning Systems & Risk Reduction
            </h3>
            <ul className="text-xs leading-relaxed text-ink-900 space-y-2 mb-6">
              <li>
                UN Office for Disaster Risk Reduction (UNDRR). (2024). <em>Global Status of Multi-Hazard Early Warning Systems 2024</em>. Geneva: UNDRR.
                <a href="https://www.undrr.org/publication/global-status-multi-hazard-early-warning-systems-2024" target="_blank" rel="noopener noreferrer" className="ml-2 text-accent-500 hover:underline font-medium">
                  [View Report] <SwissIcons.ArrowRight size={12} className="inline" />
                </a>
              </li>
              <li>
                World Meteorological Organization (WMO). (2022). <em>Early Warnings for All: Executive Action Plan 2023-2027</em>. Geneva: WMO.
                <a href="https://www.un.org/en/climatechange/early-warnings-for-all" target="_blank" rel="noopener noreferrer" className="ml-2 text-accent-500 hover:underline font-medium">
                  [View Report] <SwissIcons.ArrowRight size={12} className="inline" />
                </a>
              </li>
              <li>
                Global Commission on Adaptation. (2019). <em>Adapt Now: A Global Call for Leadership on Climate Resilience</em>. Rotterdam: GCA.
                <a href="https://gca.org/reports/adapt-now-a-global-call-for-leadership-on-climate-resilience/" target="_blank" rel="noopener noreferrer" className="ml-2 text-accent-500 hover:underline font-medium">
                  [View Report] <SwissIcons.ArrowRight size={12} className="inline" />
                </a>
              </li>
              <li>
                OCHA. (2023). <em>5 Essentials for the First 72 Hours of Disaster Response</em>. New York: OCHA.
                <a href="https://www.unocha.org/" target="_blank" rel="noopener noreferrer" className="ml-2 text-accent-500 hover:underline font-medium">
                  [Visit OCHA] <SwissIcons.ArrowRight size={12} className="inline" />
                </a>
              </li>
            </ul>

            <h3 className="text-base font-medium text-ink-900 mb-3 flex items-center gap-2">
              <SwissIcons.Database size={16} className="text-accent-500" />
              Humanitarian Data Platforms
            </h3>
            <ul className="text-xs leading-relaxed text-ink-900 space-y-2 mb-6">
              <li>
                OCHA Centre for Humanitarian Data. (2021). <em>HDX Year in Review 2021</em>. New York: OCHA.
                <a href="https://data.humdata.org/" target="_blank" rel="noopener noreferrer" className="ml-2 text-accent-500 hover:underline font-medium">
                  [Visit HDX] <SwissIcons.ArrowRight size={12} className="inline" />
                </a>
              </li>
              <li>
                OCHA ReliefWeb. (2023). <em>ReliefWeb in 2023: A Year of Resilience and Engagement</em>. New York: OCHA.
                <a href="https://reliefweb.int/report/world/reliefweb-2023-year-resilience-and-engagement" target="_blank" rel="noopener noreferrer" className="ml-2 text-accent-500 hover:underline font-medium">
                  [View Report] <SwissIcons.ArrowRight size={12} className="inline" />
                </a>
              </li>
              <li>
                ACLED (Armed Conflict Location & Event Data Project). (2024). <em>About ACLED</em>. Madison: ACLED.
                <a href="https://acleddata.com/about-acled/" target="_blank" rel="noopener noreferrer" className="ml-2 text-accent-500 hover:underline font-medium">
                  [Visit ACLED] <SwissIcons.ArrowRight size={12} className="inline" />
                </a>
              </li>
              <li>
                OCHA Financial Tracking Service. (2024). <em>Total Reported Funding 2024</em>. Geneva: OCHA.
                <a href="https://fts.unocha.org/" target="_blank" rel="noopener noreferrer" className="ml-2 text-accent-500 hover:underline font-medium">
                  [Visit FTS] <SwissIcons.ArrowRight size={12} className="inline" />
                </a>
              </li>
            </ul>

            <h3 className="text-base font-medium text-ink-900 mb-3 flex items-center gap-2">
              <SwissIcons.Users size={16} className="text-accent-500" />
              Beneficiary Management Systems
            </h3>
            <ul className="text-xs leading-relaxed text-ink-900 space-y-2 mb-6">
              <li>
                UNHCR. (2023). <em>Individuals Registered in PRIMES 2018-2023</em>. Geneva: UNHCR.
                <a href="https://www.unhcr.org/innovation/primes/" target="_blank" rel="noopener noreferrer" className="ml-2 text-accent-500 hover:underline font-medium">
                  [Learn More] <SwissIcons.ArrowRight size={12} className="inline" />
                </a>
              </li>
              <li>
                UNHCR. (2023). <em>Data of Millions of Refugees Now Securely Hosted in PRIMES</em>. UNHCR Blog.
                <a href="https://www.unhcr.org/" target="_blank" rel="noopener noreferrer" className="ml-2 text-accent-500 hover:underline font-medium">
                  [Visit UNHCR] <SwissIcons.ArrowRight size={12} className="inline" />
                </a>
              </li>
              <li>
                World Food Programme. (2022). <em>SCOPE Platform Overview</em>. Rome: WFP.
                <a href="https://www.wfp.org/support-beneficiaries/beneficiary-management" target="_blank" rel="noopener noreferrer" className="ml-2 text-accent-500 hover:underline font-medium">
                  [View SCOPE] <SwissIcons.ArrowRight size={12} className="inline" />
                </a>
              </li>
              <li>
                UNHCR & KoboToolbox. (2023). <em>Joint Initiative Announcement</em>. Geneva: UNHCR.
                <a href="https://www.kobotoolbox.org/" target="_blank" rel="noopener noreferrer" className="ml-2 text-accent-500 hover:underline font-medium">
                  [Visit KoboToolbox] <SwissIcons.ArrowRight size={12} className="inline" />
                </a>
              </li>
            </ul>

            <h3 className="text-base font-medium text-ink-900 mb-3 flex items-center gap-2">
              <SwissIcons.AISystem size={16} className="text-accent-500" />
              AI Governance & Adoption
            </h3>
            <ul className="text-xs leading-relaxed text-ink-900 space-y-2 mb-6">
              <li>
                Slack Workforce Lab. (2024). <em>AI at Work Survey</em>. San Francisco: Slack Technologies.
                <a href="https://slack.com/blog/news/ai-at-work-survey" target="_blank" rel="noopener noreferrer" className="ml-2 text-accent-500 hover:underline font-medium">
                  [View Survey] <SwissIcons.ArrowRight size={12} className="inline" />
                </a>
              </li>
              <li>
                Microsoft. (2024). <em>2024 Work Trend Index: AI at Work Is Here. Now Comes the Hard Part</em>. Redmond: Microsoft.
                <a href="https://www.microsoft.com/en-us/worklab/work-trend-index" target="_blank" rel="noopener noreferrer" className="ml-2 text-accent-500 hover:underline font-medium">
                  [View Report] <SwissIcons.ArrowRight size={12} className="inline" />
                </a>
              </li>
              <li>
                Fortune. (2024). <em>Bosses Aren't Giving Workers the AI Guidance They Need</em>.
                <a href="https://fortune.com/" target="_blank" rel="noopener noreferrer" className="ml-2 text-accent-500 hover:underline font-medium">
                  [Visit Fortune] <SwissIcons.ArrowRight size={12} className="inline" />
                </a>
              </li>
            </ul>

            <div className="bg-sand-100 border-l-4 border-accent-500 p-6 mt-8">
              <p className="text-xs leading-relaxed text-ink-900 mb-3">
                <strong>Note on Data Currency:</strong> All statistics and figures cited in this report were accurate as of their publication dates. Where noted with "(2023)" or "(2024)", these represent the most recent verified data available at the time of writing. For the most current statistics, readers are encouraged to consult the source organizations directly.
              </p>
              <p className="text-xs leading-relaxed text-ink-900">
                <strong>Fact-Check Documentation:</strong> A comprehensive fact-check report with detailed verification of all claims is available in <em>CLEAR-Executive-Fact-Check-Citations.md</em>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReportPage;
